import { Mic } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useOptionalRecording } from '@/pages/audio-ai/RecordingContext';

interface Props {
  /** Quando true (sidebar mobile aberta), não renderiza pra não competir
   *  com o trigger interno da sidebar. */
  hidden?: boolean;
}

/**
 * FAB compacto que aparece SÓ no mobile (`md:hidden`) e SÓ quando o
 * drawer da sidebar está fechado. Posicionado no canto inferior
 * esquerdo — mesma região do RecordingIndicatorPill, então durante uma
 * gravação ativa o FAB se esconde (o pill assume a posição).
 *
 * Mesma regra de ativação do trigger da sidebar: só dispara quando a
 * URL está numa ficha de paciente (`/pacientes/:id`).
 */
export const FloatingRecordingButton = ({ hidden }: Props) => {
  const ctx = useOptionalRecording();
  const { pathname } = useLocation();
  const { user } = useAuth();

  if (!ctx || hidden) return null;

  // Durante gravação ativa o pill assume essa posição — esconde o FAB
  // pra não sobrepor.
  if (ctx.isRecording) return null;

  const match = pathname.match(/^\/pacientes\/([^/?]+)/);
  const currentPatientId = match?.[1] ?? null;
  const canRecord = !!currentPatientId && !!user?.uid;

  return (
    <button
      type="button"
      disabled={!canRecord}
      onClick={() => {
        if (canRecord) {
          void ctx.start(currentPatientId, user.uid);
        }
      }}
      title={canRecord ? 'Iniciar gravação' : 'Abra um paciente para gravar'}
      className={`md:hidden fixed bottom-4 left-4 z-40 w-12 h-12 rounded-full flex items-center justify-center shadow-xl transition-all
        ${
          canRecord
            ? 'bg-error text-on-error hover:brightness-110 ring-2 ring-error/30'
            : 'bg-surface-container text-on-surface-variant/50 border border-dashed border-outline-variant/40 cursor-not-allowed shadow-md'
        }`}
      aria-label="Gravar áudio"
    >
      <Mic size={20} />
    </button>
  );
};
